/**
 * «Клипы кадра» на странице кадра видео-проекта (спека §9.2): последний клип каждой модели —
 * плеер, статус, цена (факт или оценка), ошибка; «отменить» — только в очереди (отправленный уже
 * оплачен), «повторить» — у упавшего или отменённого, «перегенерировать» — у готового (№337):
 * ПЛАТНО, ключ на клип живёт до ответа.
 *
 * Данные — опрос ролика `GET /api/projects/{id}/video`: он и идёт раз в 5 с, пока клипы в работе.
 */

import clsx from "clsx";
import { RotateCcw, X } from "lucide-react";
import { Link } from "react-router";

import { Btn, Empty, ErrorLine, Hint, Panel } from "@/components/project/ui";
import { mediaUrl } from "@/lib/media";
import { errorText } from "@/lib/project";
import { clipLine, seconds, useCancelClip, useProjectVideo, useRetryClip } from "@/lib/video";

import { CLIP_PILL, useModelLabels } from "./clips";

export function ShotClips({ pid, sid }: { pid: number; sid: number }) {
  const poll = useProjectVideo(pid);
  const cancel = useCancelClip(pid);
  const retry = useRetryClip(pid);
  const label = useModelLabels();
  const clips = poll.data?.timeline.find((t) => t.shot_id === sid)?.clips ?? [];

  return (
    <Panel
      title="Клипы кадра"
      aside={poll.data && poll.data.pending_clips > 0 ? `в работе по проекту: ${poll.data.pending_clips}` : undefined}
      annot="клипы кадра"
    >
      {poll.isPending ? (
        <Hint>загружаю…</Hint>
      ) : poll.isError ? (
        <ErrorLine text={errorText(poll.error)} />
      ) : clips.length === 0 ? (
        <Empty>Клипов нет — запустите по моделям на вкладке «Видео» проекта.</Empty>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
          {clips.map((c) => {
            const name = label(c.model);
            const retrying = retry.pending === c.generation_id;
            const cancelling = cancel.isPending && cancel.variables === c.generation_id;
            return (
              <div key={c.generation_id} className="grid content-start gap-1.5" data-annot={`клип ${c.generation_id}`}>
                {c.status === "done" && c.asset_path ? (
                  <video
                    controls
                    muted
                    preload="metadata"
                    poster={c.poster_path ? mediaUrl(c.poster_path) : undefined}
                    src={mediaUrl(c.asset_path)}
                    className="aspect-[9/16] w-full rounded-[var(--radius-ui)] border border-border bg-black object-contain"
                  />
                ) : (
                  <div className="grid aspect-[9/16] w-full place-items-center rounded-[var(--radius-ui)] border border-dashed border-border-strong text-[12px] text-fg-faint">
                    {c.status === "queued" || c.status === "running" ? "клип делается…" : "клипа нет"}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={clsx("rounded-full px-2 py-0.5 text-[11.5px] tnum", CLIP_PILL[c.status])}>
                    {clipLine(c, name)}
                  </span>
                  {c.duration_ms !== null ? <Hint>файл {seconds(c.duration_ms)}</Hint> : null}
                </div>
                {c.error ? <div className="text-[12px] text-err">{c.error}</div> : null}
                <div className="flex flex-wrap gap-1.5">
                  {c.status === "queued" ? (
                    <Btn tone="ghost" disabled={cancelling} onClick={() => cancel.mutate(c.generation_id)}>
                      <X size={13} />
                      {cancelling ? "отменяю…" : "Отменить"}
                    </Btn>
                  ) : null}
                  {/* Готовый клип тоже перегенерируется (№337) — здесь, как и в таблице клипов:
                      у `done` план собирается заново по кадру (свежий промпт движения). */}
                  {c.status === "failed" || c.status === "cancelled" || c.status === "done" ? (
                    <Btn
                      disabled={retry.busy}
                      onClick={() => retry.run(c.generation_id)}
                      title={
                        c.status === "done"
                          ? "ПЛАТНО: новый клип по текущему промпту движения этого кадра"
                          : "ПЛАТНО: новый клип с тем же снимком"
                      }
                    >
                      <RotateCcw size={13} />
                      {retrying
                        ? "ставлю…"
                        : `${c.status === "done" ? "Перегенерировать" : "Повторить"}${c.cost_estimate !== null ? ` ≈ $${c.cost_estimate.toFixed(2)}` : ""}`}
                    </Btn>
                  ) : null}
                  {c.variant_id !== null ? (
                    <Link to={`/results/${c.variant_id}`} className="self-center text-[12px] text-accent-text hover:underline">
                      карточка →
                    </Link>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ErrorLine text={cancel.isError ? errorText(cancel.error) : ""} className="mt-2" />
      <ErrorLine text={retry.error} className="mt-2" />
    </Panel>
  );
}
